import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { LAYER_COLORS, layerAtDepth } from './data';
import type { TerrainTextureSet } from './TerrainTextures';

const VOXEL_SIZE = .5;
const SIZE_X = 64;
const SIZE_Z = 64;
// 4096 half-metre cells → ~2048 m of playable depth. Cell indices are
// x + SIZE_X*(z + SIZE_Z*y), independent of SIZE_Y, so deepening the world keeps
// every existing save's removed-cell record valid.
const SIZE_Y = 4096;
const CHUNK = 16;
const ISO_LEVEL = .5;
const CORNERS = [
  [0,0,0], [1,0,0], [1,1,0], [0,1,0],
  [0,0,1], [1,0,1], [1,1,1], [0,1,1],
] as const;
const TETRAHEDRA = [
  [0,5,1,6], [0,1,2,6], [0,2,3,6],
  [0,3,7,6], [0,7,4,6], [0,4,5,6],
] as const;

interface ChunkVisual { mesh: THREE.Mesh; body: RAPIER.RigidBody; collider?: RAPIER.Collider; }

export class Terrain {
  readonly group = new THREE.Group();
  readonly meshes: THREE.Mesh[] = [];
  private readonly solid = new Uint8Array(SIZE_X * SIZE_Y * SIZE_Z).fill(1);
  private readonly chunks = new Map<string, ChunkVisual>();
  private readonly material: THREE.MeshStandardMaterial;
  private readonly removed = new Set<number>();
  // Chunks whose geometry/collider need rebuilding. Interactive digging enqueues here
  // and the game loop drains this queue with a per-frame time budget, so a single
  // strike that touches several chunks no longer blocks the click frame.
  private readonly pendingChunks = new Set<string>();

  constructor(private readonly world: RAPIER.World, private readonly textures?: TerrainTextureSet) {
    this.material = this.createTerrainMaterial();
    this.group.name = 'SmoothDiggableTerrain';
    this.buildSurfaceChunks();
  }

  loadRemoved(indices: number[]): void {
    const changedChunks = new Set<string>();
    for (const index of indices) {
      if (index < 0 || index >= this.solid.length) continue;
      this.solid[index] = 0;
      this.removed.add(index);
      const y = Math.floor(index / (SIZE_X * SIZE_Z));
      const remainder = index - y * SIZE_X * SIZE_Z;
      const z = Math.floor(remainder / SIZE_X);
      const x = remainder - z * SIZE_X;
      this.markChangedChunks(x, y, z, changedChunks);
    }
    this.buildSurfaceChunks();
    for (const key of changedChunks) {
      const [cx, cy, cz] = key.split(',').map(Number);
      if (this.validChunk(cx, cy, cz)) this.buildChunk(cx, cy, cz);
    }
  }

  getRemoved(): number[] { return [...this.removed]; }
  get solidCount(): number { return this.solid.length - this.removed.size; }

  dig(point: THREE.Vector3, radius: number): { removed: number; center: THREE.Vector3 } {
    const center = this.worldToCell(point);
    const cellRadius = Math.ceil(radius / VOXEL_SIZE);
    let count = 0;
    for (let y = center.y - cellRadius; y <= center.y + cellRadius; y++) {
      for (let z = center.z - cellRadius; z <= center.z + cellRadius; z++) {
        for (let x = center.x - cellRadius; x <= center.x + cellRadius; x++) {
          if (!this.inBounds(x, y, z) || this.cellToWorld(x, y, z).distanceTo(point) > radius) continue;
          const index = this.index(x, y, z);
          if (!this.solid[index]) continue;
          this.solid[index] = 0;
          this.removed.add(index);
          this.markChangedChunks(x, y, z, this.pendingChunks);
          count++;
        }
      }
    }
    return { removed: count, center: this.cellToWorld(center.x, center.y, center.z) };
  }

  /**
   * Rebuild queued chunks with a per-frame budget so interactive digging stays
   * responsive. Colliders and visuals are rebuilt together, so visual terrain
   * and collision never disagree by more than the frame or two it takes to drain
   * the queue. Called once per frame from the game loop.
   */
  flushPendingChunks(budgetMs = 6, maxChunks = 4): number {
    if (!this.pendingChunks.size) return 0;
    const start = performance.now();
    let built = 0;
    for (const key of this.pendingChunks) {
      this.pendingChunks.delete(key);
      const [cx, cy, cz] = key.split(',').map(Number);
      if (this.validChunk(cx, cy, cz)) this.buildChunk(cx, cy, cz);
      built++;
      if (built >= maxChunks || performance.now() - start > budgetMs) break;
    }
    return built;
  }

  get pendingChunkCount(): number { return this.pendingChunks.size; }

  dispose(): void {
    for (const chunk of this.chunks.values()) {
      chunk.mesh.geometry.dispose();
      this.world.removeRigidBody(chunk.body);
    }
    this.material.dispose();
  }

  private buildSurfaceChunks(): void {
    for (let cz = 0; cz < SIZE_Z / CHUNK; cz++) {
      for (let cx = 0; cx < SIZE_X / CHUNK; cx++) this.buildChunk(cx, 0, cz);
    }
  }

  private buildChunk(cx: number, cy: number, cz: number): void {
    const key = this.chunkKey(cx, cy, cz);
    const previous = this.chunks.get(key);
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const startX = cx * CHUNK, startY = cy * CHUNK, startZ = cz * CHUNK;
    for (let y = startY; y < Math.min(startY + CHUNK, SIZE_Y); y++) {
      for (let z = startZ; z < Math.min(startZ + CHUNK, SIZE_Z); z++) {
        for (let x = startX; x < Math.min(startX + CHUNK, SIZE_X); x++) {
          const points = CORNERS.map(corner => this.gridToWorld(x + corner[0], y + corner[1], z + corner[2]));
          const values = CORNERS.map(corner => this.densityAtVertex(x + corner[0], y + corner[1], z + corner[2]));
          if (Math.min(...values) >= ISO_LEVEL || Math.max(...values) < ISO_LEVEL) continue;
          for (const tetrahedron of TETRAHEDRA) this.polygonizeTetrahedron(tetrahedron, points, values, positions, colors, indices);
        }
      }
    }

    const rawGeometry = new THREE.BufferGeometry();
    rawGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    rawGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    rawGeometry.setIndex(indices);
    const geometry = mergeVertices(rawGeometry, .0025);
    rawGeometry.dispose();
    geometry.computeVertexNormals();
    const positionsAttribute = geometry.getAttribute('position') as THREE.BufferAttribute;
    const normalsAttribute = geometry.getAttribute('normal') as THREE.BufferAttribute;
    const surfaceNormal = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    for (let index = 0; index < positionsAttribute.count; index++) {
      const height = positionsAttribute.getY(index);
      if (height <= -.68) continue;
      const blend = .96;
      surfaceNormal.set(normalsAttribute.getX(index), normalsAttribute.getY(index), normalsAttribute.getZ(index));
      surfaceNormal.lerp(up, blend).normalize();
      normalsAttribute.setXYZ(index, surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
    }
    normalsAttribute.needsUpdate = true;
    geometry.computeBoundingSphere();
    let visual = previous;
    if (!visual) {
      const mesh = new THREE.Mesh(geometry, this.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.terrain = true;
      const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
      visual = { mesh, body };
      this.group.add(mesh);
      this.meshes.push(mesh);
      this.chunks.set(key, visual);
    } else {
      visual.mesh.geometry.dispose();
      visual.mesh.geometry = geometry;
      if (visual.collider) this.world.removeCollider(visual.collider, false);
    }
    visual.collider = positions.length && indices.length
      ? this.world.createCollider(RAPIER.ColliderDesc.trimesh(new Float32Array(positions), new Uint32Array(indices)), visual.body)
      : undefined;
  }

  private polygonizeTetrahedron(
    tetrahedron: readonly number[], points: THREE.Vector3[], values: number[],
    positions: number[], colors: number[], indices: number[],
  ): void {
    const inside = tetrahedron.filter(index => values[index] >= ISO_LEVEL);
    const outside = tetrahedron.filter(index => values[index] < ISO_LEVEL);
    if (!inside.length || !outside.length) return;
    const outward = this.averagePoints(outside, points).sub(this.averagePoints(inside, points)).normalize();
    if (inside.length === 1 || inside.length === 3) {
      const source = inside.length === 1 ? inside[0] : outside[0];
      const targets = inside.length === 1 ? outside : inside;
      const triangle = targets.map(target => this.interpolate(points[source], points[target], values[source], values[target]));
      this.addTriangle(triangle[0], triangle[1], triangle[2], outward, positions, colors, indices);
      return;
    }
    const a = this.interpolate(points[inside[0]], points[outside[0]], values[inside[0]], values[outside[0]]);
    const b = this.interpolate(points[inside[0]], points[outside[1]], values[inside[0]], values[outside[1]]);
    const c = this.interpolate(points[inside[1]], points[outside[0]], values[inside[1]], values[outside[0]]);
    const d = this.interpolate(points[inside[1]], points[outside[1]], values[inside[1]], values[outside[1]]);
    this.addTriangle(a, b, c, outward, positions, colors, indices);
    this.addTriangle(b, d, c, outward, positions, colors, indices);
  }

  private addTriangle(
    a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, outward: THREE.Vector3,
    positions: number[], colors: number[], indices: number[],
  ): void {
    if (new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a)).dot(outward) < 0) [b, c] = [c, b];
    const base = positions.length / 3;
    for (const point of [a, b, c]) {
      if (point.y > -.72) {
        const broad = Math.sin(point.x * .31) * Math.cos(point.z * .27) * .045;
        const fine = (this.noise(point.x * .7, 0, point.z * .7) - .5) * .035;
        point.y += (broad + fine) * THREE.MathUtils.smoothstep(point.y, -.72, .02);
      }
      positions.push(point.x, point.y, point.z);
      const color = new THREE.Color(LAYER_COLORS[layerAtDepth(Math.max(0, -point.y))]);
      color.multiplyScalar(.84 + this.noise(point.x, point.y, point.z) * .24);
      colors.push(color.r, color.g, color.b);
    }
    indices.push(base, base + 1, base + 2);
  }

  private interpolate(a: THREE.Vector3, b: THREE.Vector3, valueA: number, valueB: number): THREE.Vector3 {
    const difference = valueB - valueA;
    const amount = Math.abs(difference) < .0001 ? .5 : THREE.MathUtils.clamp((ISO_LEVEL - valueA) / difference, 0, 1);
    return a.clone().lerp(b, amount);
  }

  private averagePoints(indices: number[], points: THREE.Vector3[]): THREE.Vector3 {
    const result = new THREE.Vector3();
    for (const index of indices) result.add(points[index]);
    return result.multiplyScalar(1 / indices.length);
  }

  private densityAtVertex(gx: number, gy: number, gz: number): number {
    if (gy <= 0) return 0;
    if (gy >= SIZE_Y || gx <= 0 || gz <= 0 || gx >= SIZE_X || gz >= SIZE_Z) return 1;
    let total = 0;
    for (let y = gy - 1; y <= gy; y++) {
      for (let z = gz - 1; z <= gz; z++) {
        for (let x = gx - 1; x <= gx; x++) total += this.isSolid(x, y, z) ? 1 : 0;
      }
    }
    return total / 8;
  }

  private markChangedChunks(x: number, y: number, z: number, changed: Set<string>): void {
    const cx = Math.floor(x / CHUNK), cy = Math.floor(y / CHUNK), cz = Math.floor(z / CHUNK);
    changed.add(this.chunkKey(cx, cy, cz));
    if (x % CHUNK <= 1) changed.add(this.chunkKey(cx - 1, cy, cz));
    if (x % CHUNK >= CHUNK - 2) changed.add(this.chunkKey(cx + 1, cy, cz));
    if (y % CHUNK <= 1) changed.add(this.chunkKey(cx, cy - 1, cz));
    if (y % CHUNK >= CHUNK - 2) changed.add(this.chunkKey(cx, cy + 1, cz));
    if (z % CHUNK <= 1) changed.add(this.chunkKey(cx, cy, cz - 1));
    if (z % CHUNK >= CHUNK - 2) changed.add(this.chunkKey(cx, cy, cz + 1));
  }

  private worldToCell(point: THREE.Vector3): THREE.Vector3 {
    return new THREE.Vector3(
      Math.floor((point.x + SIZE_X * VOXEL_SIZE / 2) / VOXEL_SIZE),
      Math.floor(-point.y / VOXEL_SIZE),
      Math.floor((point.z + SIZE_Z * VOXEL_SIZE / 2) / VOXEL_SIZE),
    );
  }
  private cellToWorld(x: number, y: number, z: number): THREE.Vector3 { return new THREE.Vector3((x + .5) * VOXEL_SIZE - SIZE_X * VOXEL_SIZE / 2, -(y + .5) * VOXEL_SIZE, (z + .5) * VOXEL_SIZE - SIZE_Z * VOXEL_SIZE / 2); }
  private gridToWorld(x: number, y: number, z: number): THREE.Vector3 { return new THREE.Vector3(x * VOXEL_SIZE - SIZE_X * VOXEL_SIZE / 2, -y * VOXEL_SIZE, z * VOXEL_SIZE - SIZE_Z * VOXEL_SIZE / 2); }
  private noise(x: number, y: number, z: number): number { const value = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453; return value - Math.floor(value); }
  private createTerrainMaterial(): THREE.MeshStandardMaterial {
    return this.textures ? this.createTexturedMaterial(this.textures) : this.createProceduralMaterial();
  }

  // Triplanar PBR: real dirt/rock albedo, normal and roughness sampled on the UV-less
  // deformable mesh, selected and blended by depth across the six biomes. The normal maps
  // add the fine surface relief that makes excavations read as torn earth rather than clay.
  private createTexturedMaterial(textures: TerrainTextureSet): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .95, metalness: 0, emissive: 0x0a0604, emissiveIntensity: .1 });
    material.onBeforeCompile = shader => {
      shader.uniforms.uAlbedoArray = { value: textures.albedo };
      shader.uniforms.uNormalArray = { value: textures.normal };
      shader.uniforms.uRoughnessArray = { value: textures.roughness };
      shader.uniforms.uTexScale = { value: .42 };
      shader.vertexShader = `varying vec3 vBelowWorldPosition;\nvarying vec3 vBelowWorldNormal;\n${shader.vertexShader}`.replace(
        '#include <worldpos_vertex>',
        '#include <worldpos_vertex>\nvBelowWorldPosition = worldPosition.xyz;\nvBelowWorldNormal = mat3(modelMatrix) * normal;',
      );
      shader.fragmentShader = `
        varying vec3 vBelowWorldPosition;
        varying vec3 vBelowWorldNormal;
        uniform sampler2DArray uAlbedoArray;
        uniform sampler2DArray uNormalArray;
        uniform sampler2DArray uRoughnessArray;
        uniform float uTexScale;
        vec3 belowTriAlbedo(float layer, vec3 wp, vec3 w){
          return texture(uAlbedoArray, vec3(wp.zy, layer)).rgb * w.x
               + texture(uAlbedoArray, vec3(wp.xz, layer)).rgb * w.y
               + texture(uAlbedoArray, vec3(wp.xy, layer)).rgb * w.z;
        }
        float belowTriRough(float layer, vec3 wp, vec3 w){
          return texture(uRoughnessArray, vec3(wp.zy, layer)).r * w.x
               + texture(uRoughnessArray, vec3(wp.xz, layer)).r * w.y
               + texture(uRoughnessArray, vec3(wp.xy, layer)).r * w.z;
        }
        vec3 belowTriNormal(float layer, vec3 wp, vec3 wn, vec3 w){
          vec3 nx = texture(uNormalArray, vec3(wp.zy, layer)).xyz * 2.0 - 1.0;
          vec3 ny = texture(uNormalArray, vec3(wp.xz, layer)).xyz * 2.0 - 1.0;
          vec3 nz = texture(uNormalArray, vec3(wp.xy, layer)).xyz * 2.0 - 1.0;
          nx = vec3(nx.xy + wn.zy, abs(nx.z) * wn.x);
          ny = vec3(ny.xy + wn.xz, abs(ny.z) * wn.y);
          nz = vec3(nz.xy + wn.xy, abs(nz.z) * wn.z);
          return normalize(nx.zyx * w.x + ny.xzy * w.y + nz.xyz * w.z);
        }
      ${shader.fragmentShader}`.replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        vec3 belowWP = vBelowWorldPosition * uTexScale;
        vec3 belowWN = normalize(vBelowWorldNormal);
        vec3 belowW = pow(abs(belowWN), vec3(4.0));
        belowW /= (belowW.x + belowW.y + belowW.z + 1e-4);
        float belowDepth = max(0.0, -vBelowWorldPosition.y);
        float belowM = clamp(smoothstep(2.0,5.5,belowDepth) + smoothstep(7.0,13.0,belowDepth) + smoothstep(22.0,45.0,belowDepth), 0.0, 3.0);
        float belowLayerA = floor(belowM);
        float belowLayerB = min(3.0, belowLayerA + 1.0);
        float belowLt = belowM - belowLayerA;
        vec3 belowAlbedo = mix(belowTriAlbedo(belowLayerA, belowWP, belowW), belowTriAlbedo(belowLayerB, belowWP, belowW), belowLt);
        belowAlbedo = pow(belowAlbedo, vec3(2.2));
        diffuseColor.rgb = belowAlbedo * mix(vec3(1.0), diffuseColor.rgb * 1.7, 0.28);
        float belowDamp = smoothstep(1.2,3.4,belowDepth) * (1.0 - smoothstep(5.5,8.0,belowDepth));
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(.82,.72,.66), belowDamp * .25);
        float belowDeep = smoothstep(9.0,22.0,belowDepth);
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(.74,.78,.88), belowDeep * .3);
        float belowCrystal = smoothstep(40.0,110.0,belowDepth) * (1.0 - smoothstep(120.0,200.0,belowDepth));
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(.8,.9,1.18) + vec3(0.004,0.01,0.028), belowCrystal * .34);
        float belowCore = smoothstep(120.0,380.0,belowDepth);
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.2,.72,.56) + vec3(0.04,0.008,0.0), belowCore * .45);`,
      ).replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        // Dirt and rock are matte — a high roughness floor kills the grazing-sun specular
        // streaks that showed up as thin white lines along the surface.
        roughnessFactor = clamp(mix(belowTriRough(belowLayerA, belowWP, belowW), belowTriRough(belowLayerB, belowWP, belowW), belowLt), 0.66, 1.0);`,
      ).replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
        vec3 belowTriN = normalize(mix(belowTriNormal(belowLayerA, belowWP, belowWN, belowW), belowTriNormal(belowLayerB, belowWP, belowWN, belowW), belowLt));
        // Soften the normal-map relief toward the geometric normal so edges do not glint.
        vec3 belowWorldN = normalize(mix(belowWN, belowTriN, 0.55));
        normal = normalize((viewMatrix * vec4(belowWorldN, 0.0)).xyz);`,
      );
    };
    material.customProgramCacheKey = () => 'below-terrain-textured-v1';
    return material;
  }

  private createProceduralMaterial(): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .94, metalness: 0, emissive: 0x140906, emissiveIntensity: .3 });
    material.onBeforeCompile = shader => {
      shader.vertexShader = `varying vec3 vBelowWorldPosition;\n${shader.vertexShader}`.replace(
        '#include <worldpos_vertex>',
        '#include <worldpos_vertex>\nvBelowWorldPosition = worldPosition.xyz;',
      );
      shader.fragmentShader = `
        varying vec3 vBelowWorldPosition;
        float belowHash(vec3 p) {
          p = fract(p * .1031);
          p += dot(p, p.yzx + 33.33);
          return fract((p.x + p.y) * p.z);
        }
        float belowNoise(vec3 p) {
          vec3 cell = floor(p);
          vec3 blend = fract(p);
          blend = blend * blend * (3.0 - 2.0 * blend);
          return mix(
            mix(mix(belowHash(cell), belowHash(cell + vec3(1,0,0)), blend.x),
                mix(belowHash(cell + vec3(0,1,0)), belowHash(cell + vec3(1,1,0)), blend.x), blend.y),
            mix(mix(belowHash(cell + vec3(0,0,1)), belowHash(cell + vec3(1,0,1)), blend.x),
                mix(belowHash(cell + vec3(0,1,1)), belowHash(cell + vec3(1,1,1)), blend.x), blend.y), blend.z);
        }
      ${shader.fragmentShader}`.replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        float fineGrain = belowNoise(vBelowWorldPosition * 10.0);
        float medGrain = belowNoise(vBelowWorldPosition * 3.6);
        float coarseGrain = belowNoise(vBelowWorldPosition * 1.35);
        float strata = sin(vBelowWorldPosition.y * 18.0 + coarseGrain * 5.0) * 0.5 + 0.5;
        float mineralFleck = smoothstep(0.92, 0.99, belowNoise(vBelowWorldPosition * 19.0));
        vec3 grainTint = mix(vec3(0.56,0.60,0.64), vec3(1.20,1.09,0.98), fineGrain);
        grainTint *= mix(0.80, 1.16, coarseGrain);
        grainTint *= mix(0.88, 1.10, medGrain);
        grainTint *= mix(0.92, 1.07, strata);
        diffuseColor.rgb *= grainTint;
        float depth = max(0.0,-vBelowWorldPosition.y);
        float damp = smoothstep(1.2,3.4,depth) * (1.0-smoothstep(5.5,8.0,depth));
        diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*vec3(.78,.67,.6),damp*.3);
        float deep = smoothstep(9.0, 22.0, depth);
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(.72,.76,.86), deep * 0.35);
        float crystalZone = smoothstep(40.0, 110.0, depth) * (1.0 - smoothstep(120.0, 200.0, depth));
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(.78,.86,1.12) + vec3(0.004,0.008,0.02), crystalZone * 0.3);
        float coreZone = smoothstep(120.0, 380.0, depth);
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.18,.74,.6) + vec3(0.03,0.006,0.0), coreZone * 0.42);
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 1.22 + vec3(0.02), mineralFleck * 0.22);`,
      ).replace(
        '#include <roughnessmap_fragment>',
        '#include <roughnessmap_fragment>\nroughnessFactor = clamp(roughnessFactor + (belowNoise(vBelowWorldPosition * 11.0) - .5) * .09, .78, 1.0);',
      );
    };
    material.customProgramCacheKey = () => 'below-terrain-material-v5';
    return material;
  }
  private index(x: number, y: number, z: number): number { return x + SIZE_X * (z + SIZE_Z * y); }
  private inBounds(x: number, y: number, z: number): boolean { return x >= 0 && y >= 0 && z >= 0 && x < SIZE_X && y < SIZE_Y && z < SIZE_Z; }
  private isSolid(x: number, y: number, z: number): boolean { return this.inBounds(x, y, z) && this.solid[this.index(x, y, z)] === 1; }
  private validChunk(x: number, y: number, z: number): boolean { return x >= 0 && y >= 0 && z >= 0 && x < SIZE_X / CHUNK && y < SIZE_Y / CHUNK && z < SIZE_Z / CHUNK; }
  private chunkKey(x: number, y: number, z: number): string { return `${x},${y},${z}`; }
}
